<template>
  <div ref="rootRef" class="smart-doc-plus">
    <button
      ref="btnRef"
      type="button"
      class="smart-doc-plus-btn"
      :class="{ 'is-open': menuOpen }"
      title="添加"
      aria-label="添加附件或技能"
      :aria-expanded="menuOpen"
      @click="toggleMenu"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </button>

    <!-- Teleport 到 body，避免被 AI 面板 overflow:hidden 裁切 -->
    <Teleport to="body">
      <div
        v-if="menuOpen"
        ref="flyoutRef"
        class="smart-doc-flyout"
        role="menu"
        :style="flyoutStyle"
        @mouseleave="onFlyoutLeave"
      >
        <div
          v-show="skillPanelOpen"
          class="smart-doc-skill-panel"
          role="menu"
          aria-label="选择技能"
        >
          <div class="skill-panel-scroll">
            <div v-if="loading" class="skill-panel-empty">加载中…</div>
            <div v-else-if="!skills.length" class="skill-panel-empty">
              暂无技能，请先创建或导入
            </div>
            <button
              v-for="skill in skills"
              :key="skill.id"
              type="button"
              class="skill-row"
              :class="{ active: skill.id === selectedSkillId, hover: hoverSkillId === skill.id }"
              role="menuitem"
              @mouseenter="hoverSkillId = skill.id"
              @mouseleave="hoverSkillId = ''"
              @click="emitSelect(skill)"
            >
              <span class="skill-row-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 1.5c-1.8 0-3.3 1.2-3.8 2.8C2.8 4.7 2 5.9 2 7.3c0 1.6 1 3 2.5 3.5v1.7c0 .6.4 1 1 1h5c.6 0 1-.4 1-1V10.8c1.5-.5 2.5-1.9 2.5-3.5 0-1.4-.8-2.6-2.2-3-.5-1.6-2-2.8-3.8-2.8Z"
                    stroke="#86909C"
                    stroke-width="1.2"
                    stroke-linejoin="round"
                  />
                  <circle cx="6.2" cy="7.2" r="0.7" fill="#86909C" />
                  <circle cx="9.8" cy="7.2" r="0.7" fill="#86909C" />
                </svg>
              </span>
              <span class="skill-row-label">{{ skill.name }}</span>
            </button>
          </div>

          <div class="skill-panel-divider" aria-hidden="true" />

          <div class="skill-panel-footer">
            <button type="button" class="skill-row action" role="menuitem" @click="emitCreate">
              <span class="skill-row-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 2.2 8.7 5.1 11.6 5.8 8.7 6.5 8 9.4 7.3 6.5 4.4 5.8 7.3 5.1 8 2.2Z"
                    stroke="#4E5969"
                    stroke-width="1.1"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12.2 9.2 12.5 10.6 13.9 10.9 12.5 11.2 12.2 12.6 11.9 11.2 10.5 10.9 11.9 10.6 12.2 9.2Z"
                    stroke="#4E5969"
                    stroke-width="1"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.2 10.5 4.5 11.6 5.6 11.9 4.5 12.2 4.2 13.3 3.9 12.2 2.8 11.9 3.9 11.6 4.2 10.5Z"
                    stroke="#4E5969"
                    stroke-width="1"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="skill-row-label">与 AI 对话创建技能</span>
            </button>
            <button type="button" class="skill-row action" role="menuitem" @click="emitImport">
              <span class="skill-row-icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M8 10.5v-7M5.5 6 8 3.5 10.5 6"
                    stroke="#4E5969"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M3 11.5v1c0 .6.4 1 1 1h8c.6 0 1-.4 1-1v-1"
                    stroke="#4E5969"
                    stroke-width="1.2"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
              <span class="skill-row-label">上传本地技能</span>
            </button>
          </div>
        </div>

        <div class="smart-doc-plus-menu">
          <button type="button" class="plus-item" role="menuitem" @click="onUpload">
            <span class="plus-item-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8 2.5v7M5.5 7 8 9.5 10.5 7"
                  stroke="#4E5969"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M3 11.5v1c0 .6.4 1 1 1h8c.6 0 1-.4 1-1v-1"
                  stroke="#4E5969"
                  stroke-width="1.2"
                  stroke-linecap="round"
                />
              </svg>
            </span>
            <span class="plus-item-label">上传附件</span>
          </button>
          <button
            type="button"
            class="plus-item has-submenu"
            :class="{ active: skillPanelOpen }"
            role="menuitem"
            aria-haspopup="true"
            :aria-expanded="skillPanelOpen"
            @mouseenter="openSkillPanel"
            @click="openSkillPanel"
          >
            <span class="plus-item-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8 1.5c-1.8 0-3.3 1.2-3.8 2.8C2.8 4.7 2 5.9 2 7.3c0 1.6 1 3 2.5 3.5v1.7c0 .6.4 1 1 1h5c.6 0 1-.4 1-1V10.8c1.5-.5 2.5-1.9 2.5-3.5 0-1.4-.8-2.6-2.2-3-.5-1.6-2-2.8-3.8-2.8Z"
                  stroke="#266FE8"
                  stroke-width="1.2"
                  stroke-linejoin="round"
                />
                <circle cx="6.2" cy="7.2" r="0.7" fill="#266FE8" />
                <circle cx="9.8" cy="7.2" r="0.7" fill="#266FE8" />
              </svg>
            </span>
            <span class="plus-item-label">技能</span>
            <span class="plus-item-chevron" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.5 2.5 8 6 4.5 9.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { listDocumentSkills } from '@/api/documentWorkbenchApi';
import type { DocumentSkillListItem } from '@/types/documentWorkbench';

const props = defineProps<{
  workspaceId: string;
  selectedSkillId?: string;
}>();

const emit = defineEmits<{
  upload: [];
  select: [skill: DocumentSkillListItem];
  create: [];
  import: [];
}>();

const rootRef = ref<HTMLElement | null>(null);
const btnRef = ref<HTMLElement | null>(null);
const flyoutRef = ref<HTMLElement | null>(null);
const menuOpen = ref(false);
const skillPanelOpen = ref(false);
const loading = ref(false);
const skills = ref<DocumentSkillListItem[]>([]);
const hoverSkillId = ref('');
const anchorRect = ref<DOMRect | null>(null);
let leaveTimer: number | null = null;

const flyoutStyle = computed(() => {
  const r = anchorRect.value;
  if (!r) return { display: 'none' };
  // 右缘对齐 + 按钮，技能子菜单向左展开（可超出 AI 面板）
  return {
    position: 'fixed' as const,
    right: `${Math.max(8, window.innerWidth - r.right)}px`,
    bottom: `${Math.max(8, window.innerHeight - r.top + 8)}px`,
    zIndex: 5000,
  };
});

function syncAnchor() {
  const el = btnRef.value;
  anchorRect.value = el ? el.getBoundingClientRect() : null;
}

function toggleMenu() {
  if (menuOpen.value) {
    closeAll();
    return;
  }
  syncAnchor();
  menuOpen.value = true;
  skillPanelOpen.value = false;
  void nextTick(() => syncAnchor());
}

function closeAll() {
  menuOpen.value = false;
  skillPanelOpen.value = false;
  hoverSkillId.value = '';
  if (leaveTimer != null) {
    window.clearTimeout(leaveTimer);
    leaveTimer = null;
  }
}

function onFlyoutLeave() {
  if (leaveTimer != null) window.clearTimeout(leaveTimer);
  leaveTimer = window.setTimeout(() => {
    closeAll();
  }, 180);
}

function onUpload() {
  closeAll();
  emit('upload');
}

async function openSkillPanel() {
  if (leaveTimer != null) {
    window.clearTimeout(leaveTimer);
    leaveTimer = null;
  }
  skillPanelOpen.value = true;
  syncAnchor();
  loading.value = true;
  try {
    skills.value = await listDocumentSkills(props.workspaceId);
  } catch {
    skills.value = [];
  } finally {
    loading.value = false;
  }
}

function emitSelect(skill: DocumentSkillListItem) {
  emit('select', skill);
  closeAll();
}

function emitCreate() {
  closeAll();
  emit('create');
}

function emitImport() {
  closeAll();
  emit('import');
}

function onDocPointerDown(e: MouseEvent) {
  const t = e.target as Node;
  if (rootRef.value?.contains(t)) return;
  if (flyoutRef.value?.contains(t)) return;
  closeAll();
}

function onWinChange() {
  if (menuOpen.value) syncAnchor();
}

watch(
  () => props.workspaceId,
  () => {
    skills.value = [];
    if (skillPanelOpen.value) void openSkillPanel();
  },
);

onMounted(() => {
  document.addEventListener('mousedown', onDocPointerDown);
  window.addEventListener('resize', onWinChange);
  window.addEventListener('scroll', onWinChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocPointerDown);
  window.removeEventListener('resize', onWinChange);
  window.removeEventListener('scroll', onWinChange, true);
  if (leaveTimer != null) window.clearTimeout(leaveTimer);
});

defineExpose({ openSkillPanel, closeAll });
</script>

<style scoped>
.smart-doc-plus {
  position: relative;
  display: inline-flex;
  align-items: center;
  z-index: 3;
}

.smart-doc-plus-btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: #e5e6eb;
  color: #4e5969;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.smart-doc-plus-btn:hover,
.smart-doc-plus-btn.is-open {
  background: #d0d2da;
  color: #1f2329;
}
</style>

<!-- flyout 在 body 上，需非 scoped 或 :global；用第二段 style -->
<style>
.smart-doc-flyout {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  pointer-events: auto;
}

.smart-doc-plus-menu {
  width: 148px;
  padding: 4px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e7e7e7;
  box-shadow: 0 6px 20px rgba(15, 35, 52, 0.12);
  flex-shrink: 0;
}

.smart-doc-flyout .plus-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  line-height: 22px;
  color: #1f2329;
  cursor: pointer;
  text-align: left;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.smart-doc-flyout .plus-item:hover,
.smart-doc-flyout .plus-item.active {
  background: #ecf2fe;
  color: #0052d9;
}

.smart-doc-flyout .plus-item-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.smart-doc-flyout .plus-item-label {
  flex: 1;
  min-width: 0;
}

.smart-doc-flyout .plus-item-chevron {
  display: inline-flex;
  color: #86909c;
  flex-shrink: 0;
}

.smart-doc-flyout .plus-item.active .plus-item-chevron,
.smart-doc-flyout .plus-item:hover .plus-item-chevron {
  color: #0052d9;
}

.smart-doc-skill-panel {
  width: 220px;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e7e7e7;
  box-shadow: 0 6px 20px rgba(15, 35, 52, 0.12);
  overflow: hidden;
}

.smart-doc-skill-panel .skill-panel-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 4px;
}

.smart-doc-skill-panel .skill-panel-empty {
  padding: 12px 10px;
  font-size: 12px;
  color: #86909c;
}

.smart-doc-skill-panel .skill-panel-divider {
  height: 1px;
  background: #e7e7e7;
  margin: 0 4px;
}

.smart-doc-skill-panel .skill-panel-footer {
  padding: 4px;
}

.smart-doc-skill-panel .skill-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 20px;
  color: #1f2329;
  cursor: pointer;
  text-align: left;
}

.smart-doc-skill-panel .skill-row:hover,
.smart-doc-skill-panel .skill-row.hover,
.smart-doc-skill-panel .skill-row.active {
  background: #ecf2fe;
  color: #0052d9;
}

.smart-doc-skill-panel .skill-row-icon {
  width: 16px;
  height: 16px;
  display: inline-flex;
  flex-shrink: 0;
}

.smart-doc-skill-panel .skill-row-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
